import {OperationModeType} from '@hconnect/common/types'
import type {OperationMode, AssetTransition, Asset} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {Card, CardTitle} from '@hconnect/uikit/src/lib2'
import {Box, TextField, MenuItem, Stack, Typography} from '@mui/material'
import {useState, useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {usePermission} from '../../../permissions'
import {useAssetTransitionsQuery} from '../../hooks'

import {SwitchMatrixForm} from './SwitchMatrixForm'

const getProductionOperationModes = (operationModes: OperationMode[]) =>
  operationModes.filter((operationMode) => operationMode.type !== OperationModeType.Maintenance)

const getTransitionsFromOperationMode = (
  operationModeId: number,
  transitions?: AssetTransition[]
) => transitions?.filter(({from}) => from === operationModeId) ?? []

interface SwitchMatrixCardProps {
  asset: Asset
}

export const SwitchMatrixCard = ({asset}: SwitchMatrixCardProps) => {
  const {t} = useTranslation()
  const canChangeAssets = usePermission('CHANGE_ASSETS')
  const {data: assetTransitions} = useAssetTransitionsQuery(asset.id)
  const {operationModes} = asset
  const productionOperationModes = useMemo(
    () => getProductionOperationModes(operationModes),
    [operationModes]
  )

  const [selectedOperationModeId, setSelectedOperationModeId] = useState<OperationMode['id']>()

  // effect to set initial value of selected operation mode
  useEffect(() => {
    const firstOperationMode =
      productionOperationModes.length > 0 ? productionOperationModes[0] : undefined
    setSelectedOperationModeId(firstOperationMode?.id)
  }, [productionOperationModes])

  const isReadOnly = !canChangeAssets

  const selectedTransitions =
    selectedOperationModeId !== undefined
      ? getTransitionsFromOperationMode(selectedOperationModeId, assetTransitions)
      : undefined

  return (
    <Card
      {...dataTestId('switch_matrix_card')}
      headerContent={
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <Stack>
            <CardTitle sx={{mb: 0}}>{t('assetsSettings.switchMatrix')}</CardTitle>
            <Typography variant="caption">{t('assetsSettings.switchTimeMinutes')}</Typography>
          </Stack>
          {productionOperationModes.length > 0 && (
            <TextField
              select
              sx={{width: ({spacing}) => spacing(30)}}
              label={t('assetsSettings.switchFrom')}
              value={selectedOperationModeId ?? ''}
              onChange={({target: {value}}) =>
                setSelectedOperationModeId(value === '' ? undefined : parseInt(value))
              }
              {...dataTestId('operation_mode_select')}
            >
              {productionOperationModes.map((operationMode) => (
                <MenuItem
                  key={operationMode.id}
                  value={operationMode.id}
                  {...dataTestId('operation_mode_select_option')}
                >
                  {operationMode.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      }
    >
      {selectedTransitions && (
        <SwitchMatrixForm
          asset={asset}
          transitions={selectedTransitions}
          operationModes={productionOperationModes}
          isReadOnly={isReadOnly}
        />
      )}
    </Card>
  )
}
